import React, { useCallback, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import URI from "urijs";
import BootstrapModalForm from "components/bootstrap/BootstrapModalForm";
import { Checkbox, ControlLabel, FormGroup, HelpBlock, MenuItem } from "components/bootstrap";
import Input from "components/bootstrap/Input";
import Routes from "routing/Routes";
import queryTitle from "views/logic/queries/QueryTitle";
import WidgetFocusContext from "views/components/contexts/WidgetFocusContext";
import useHistory from "routing/useHistory";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
const ConfigurationModal = ({ onSave, view, show, onClose }) => {
  const { t } = useTranslation("dashboards");
  const availableTabs = view.search.queries.keySeq().map((query, idx) => [idx, queryTitle(view, query.id)]).toJS();
  const [refreshInterval, setRefreshInterval] = useState(10);
  const [queryTabs, setQueryTabs] = useState(availableTabs.map(([idx]) => idx));
  const [queryCycleInterval, setQueryCycleInterval] = useState(30);
  const addQueryTab = useCallback((idx) => setQueryTabs([...queryTabs, idx]), [queryTabs, setQueryTabs]);
  const removeQueryTab = useCallback(
    (idx) => setQueryTabs(queryTabs.filter((tab) => tab !== idx)),
    [queryTabs, setQueryTabs]
  );
  const _onSave = useCallback(
    () => onSave({
      refreshInterval,
      queryTabs,
      queryCycleInterval
    }),
    [onSave, refreshInterval, queryTabs, queryCycleInterval]
  );
  return /* @__PURE__ */ React.createElement(
    BootstrapModalForm,
    {
      bsSize: "large",
      onCancel: onClose,
      onSubmitForm: _onSave,
      submitButtonText: t("full_screen.start"),
      title: t("full_screen.title"),
      show
    },
    /* @__PURE__ */ React.createElement(
      Input,
      {
        autoFocus: true,
        id: "refresh-interval",
        type: "number",
        min: "1",
        name: "refresh-interval",
        label: t("full_screen.refresh_interval"),
        help: t("full_screen.refresh_interval_help"),
        onChange: ({ target: { value } }) => setRefreshInterval(Number.parseInt(value, 10)),
        required: true,
        step: 1,
        value: Number.isNaN(refreshInterval) ? "" : refreshInterval
      }
    ),
    /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(ControlLabel, null, t("full_screen.tabs")), /* @__PURE__ */ React.createElement("ul", null, availableTabs.map(([idx, title]) => /* @__PURE__ */ React.createElement("li", { key: `${idx}-${title}` }, /* @__PURE__ */ React.createElement(
      Checkbox,
      {
        inline: true,
        checked: queryTabs.includes(idx),
        onChange: (event) => event.target.checked ? addQueryTab(idx) : removeQueryTab(idx)
      },
      title
    )))), /* @__PURE__ */ React.createElement(HelpBlock, null, t("full_screen.tabs_help"))),
    /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "query-cycle-interval",
        type: "number",
        min: "1",
        name: "query-cycle-interval",
        label: t("full_screen.cycle_interval"),
        help: t("full_screen.cycle_interval_help"),
        onChange: ({ target: { value } }) => setQueryCycleInterval(Number.parseInt(value, 10)),
        required: true,
        step: "1",
        value: Number.isNaN(queryCycleInterval) ? "" : queryCycleInterval
      }
    )
  );
};
const redirectToBigDisplayMode = (history, view, config, unsetWidgetFocusing) => {
  unsetWidgetFocusing();
  history.push(new URI(Routes.DASHBOARD.FULL_SCREEN(view.id)).search(config).toString());
};
const createQueryFromConfiguration = ({ queryCycleInterval = void 0, queryTabs = void 0, refreshInterval }, view) => {
  const basicConfiguration = {
    interval: Number(queryCycleInterval).toString(),
    refresh: Number(refreshInterval).toString()
  };
  const allQueryIndices = view.search.queries.toIndexedSeq().map((_, v) => v).toJS();
  return !queryTabs || allQueryIndices.join(",") === queryTabs.join(",") ? basicConfiguration : { ...basicConfiguration, tabs: queryTabs.join(",") };
};
const BigDisplayModeConfiguration = ({ disabled = false, show = false, view }) => {
  const { t } = useTranslation("dashboards");
  const [showConfigurationModal, setShowConfigurationModal] = useState(show);
  const { unsetWidgetFocusing } = useContext(WidgetFocusContext);
  const history = useHistory();
  const sendTelemetry = useSendTelemetry("dashboard-menu");
  const onSave = (config) => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.DASHBOARD_FULL_SCREEN_MODE_STARTED, {
      app_pathname: "dashboard"
    });
    redirectToBigDisplayMode(history, view, createQueryFromConfiguration(config, view), unsetWidgetFocusing);
  };
  return /* @__PURE__ */ React.createElement(React.Fragment, null, showConfigurationModal && /* @__PURE__ */ React.createElement(ConfigurationModal, { onClose: () => setShowConfigurationModal(false), onSave, show: true, view }), /* @__PURE__ */ React.createElement(MenuItem, { disabled, onSelect: () => setShowConfigurationModal(true), icon: "live_tv" }, t("full_screen.menu")));
};
export default BigDisplayModeConfiguration;
